<template>
  <div class="hidden">
    <GeojsonLayer
      v-if="source"
      :layer="layer"
      :source="source"
      :sourceId="SOURCE_ID"
      before="layer-room-text"
    />
    <MapPopup ref="instanceRef" v-bind="instanceProps" />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import GeojsonLayer from '../../mapbox-layers/GeojsonLayer.vue'
import {
  LngLatLike,
  Popup,
  FillLayerSpecification,
  MapMouseEvent,
  GeoJSONSourceSpecification
} from 'mapbox-gl'
import { configToMapboxExpression, getColorForValue } from '@/utils/colorScale'
import { useStore } from 'vuex'
import { Room } from '@/interfaces/room.interface'
import useMapbox from '@/composables/useMapbox'
import MapPopup from '@/components/map/popups/MapPopup.vue'
import * as turf from '@turf/turf'
import useColoredEntities from '@/composables/useColoredEntities'
import { ComponentProps } from '@/utils/ComponentProps'
import {
  createColorCircleLabel,
  createDivider,
  createSmallIconSubtitle,
  createSmallSubtitle,
  createSmallTitle
} from '@/components/Builder'
import { COCLASSES, COCLASSGROUPS } from '@/interfaces/coclass.interface'
import { useI18n } from 'vue-i18n'

const LAYER_ID = 'colored-spaces-layer'
const SOURCE_ID = 'colored-spaces-source'

const store = useStore()
const { locale } = useI18n()
const { map } = useMapbox()
const coloredEntities = useColoredEntities()
const spaces = computed<Room[] | undefined>(() => store.state.roomsForSelectedLevel?.data.features)
const popup = new Popup({
  closeButton: false,
  closeOnClick: false,
  className: 'colored-spaces-popup',
  offset: 20
})
const hoveredRoomId = ref<string>()
const space = ref<Room>()
const instanceRef = ref<InstanceType<typeof MapPopup> | null>(null)
const instanceProps = ref<ComponentProps<typeof MapPopup>>({})

const hoveredRoomValue = computed(
  () => coloredEntities.data?.find((v) => v.id === hoveredRoomId.value)?.value
)

const color = computed(() =>
  getColorForValue(coloredEntities.colorScale, hoveredRoomValue.value ?? 0)
)

const colorLabel = computed<string | undefined>(() =>
  hoveredRoomValue.value !== undefined
    ? coloredEntities.title +
      ': ' +
      coloredEntities.formatter(hoveredRoomValue.value) +
      coloredEntities.unit
    : undefined
)

onMounted(() => {
  map?.on('mousemove', LAYER_ID, (e: MapMouseEvent) => updatePopup(e))
  map?.on('mouseleave', LAYER_ID, () => removePopup())
  map?.on('mouseenter', LAYER_ID, () => {
    updateInstanceProps(space.value as Room)
  })
})

watch(space, (space) => {
  updateInstanceProps(space)
})

const layer = computed<FillLayerSpecification>((): FillLayerSpecification => {
  return {
    id: LAYER_ID,
    source: SOURCE_ID,
    type: 'fill',
    paint: {
      'fill-color': configToMapboxExpression('value', coloredEntities.colorScale),
      // Hidden, the new coloring system does not need it.
      // This layer is now only used for the popup.
      'fill-opacity': 0
    },
    minzoom: 10
  }
})

const source = computed<GeoJSONSourceSpecification | undefined>(() => {
  if (!spaces.value) return undefined
  const source: GeoJSONSourceSpecification = {
    type: 'geojson',
    data: {
      type: 'FeatureCollection',
      features: spaces.value
        .map(roomToGeoJSONFeature)
        .map((feature) => {
          const spaceValue = coloredEntities.data?.find((s) => s.id === feature.properties?.id)
          if (spaceValue === undefined) return undefined
          return {
            ...feature,
            properties: {
              ...feature.properties,
              value: spaceValue.value
            }
          }
        })
        .filter((f) => f !== undefined) as GeoJSON.Feature[]
    }
  }
  return source
})

function roomToGeoJSONFeature(room: Room): GeoJSON.Feature {
  return {
    id: room.id,
    type: 'Feature',
    properties: {
      id: room.id
    },
    geometry: {
      type: room.geometry.type,
      coordinates: room.geometry.coordinates as any
    }
  }
}

function updateInstanceProps(space: Room | undefined) {
  if (!space) return

  let classCode: string | undefined = undefined

  if (space.properties.classification) {
    const classification = space.properties.classification
    if (classification.USED && classification.USED.classCode) {
      classCode = classification.USED.classCode
    } else if (classification.PLANNED && classification.PLANNED.classCode) {
      classCode = classification.PLANNED.classCode
    }
  }

  const coclass = COCLASSES.find((c) => c.coclass === classCode)
  const roomGroup = coclass ? COCLASSGROUPS[coclass.roomGroup][locale.value] : ' - '
  instanceProps.value = {
    rows: colorLabel.value
      ? [
          createSmallTitle(space.properties.displayName),
          createSmallSubtitle(space.properties.aliasId, { style: { 'margin-bottom': '8px' } }),
          createSmallIconSubtitle(roomGroup, 'IconTag'),
          ...(space.properties.capacity
            ? [
                createSmallIconSubtitle(space.properties.capacity.toString() ?? '', 'IconUsers', {
                  'margin-top': '4px'
                })
              ]
            : []),
          createDivider(),
          createColorCircleLabel(color.value.toHex(), colorLabel.value)
        ]
      : []
  }
}

function updatePopup(e: MapMouseEvent): void {
  if (!spaces.value || !map) return
  map.getCanvas().style.cursor = 'pointer'
  const properties = e.features?.at(0)?.properties
  if (!properties) return
  const s = spaces.value.find((room) => room.properties.id === properties.id)
  if (!s) return
  if (hoveredRoomId.value == s.properties.id) return
  const centerOfMass = turf.centerOfMass(s.geometry)
  const lngLat: LngLatLike = {
    lng: centerOfMass.geometry.coordinates[0],
    lat: centerOfMass.geometry.coordinates[1]
  }
  if (!instanceRef.value) return
  const html = instanceRef.value.$el
  popup.setDOMContent(html).setLngLat(lngLat).addTo(map)
  popup.removeClassName('hidden')
  hoveredRoomId.value = s.properties.id
  space.value = s
}
function removePopup(): void {
  if (map) map.getCanvas().style.cursor = ''
  popup.remove()
  hoveredRoomId.value = undefined
}
</script>
<style scoped lang="scss">
.legend {
  position: fixed;
  top: 64px;
  right: 0;
  margin: 16px;
}
.hidden {
  position: fixed;
  visibility: hidden;
}
</style>
<style lang="scss">
.colored-spaces-popup .mapboxgl-popup-content {
  pointer-events: none;
}
</style>
